/**
 * useOverflowAudit — DEVELOPMENT-ONLY horizontal-overflow diagnostic.
 *
 * Runs ONLY when `process.env.NODE_ENV === "development"`. On each
 * route change (and on resize) it finds every element whose box
 * extends past the viewport width and logs a grouped warning with
 * the tag, className and bounds so the offending node is easy to
 * fix. Also opens on-demand via `?debugOverflow=1`.
 *
 * This hook does NOT render UI; it does NOT ship to production
 * because the body is wrapped in a NODE_ENV guard that CRA / webpack
 * dead-code-eliminates on `yarn build`.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export default function useOverflowAudit() {
  const location = useLocation();

  useEffect(() => {
    const forceFlag =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("debugOverflow") === "1";
    const enabled =
      (typeof process !== "undefined" &&
        process.env &&
        process.env.NODE_ENV === "development") ||
      forceFlag;
    if (!enabled || typeof window === "undefined") return;

    let raf = 0;
    const scan = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const vw = document.documentElement.clientWidth;
        const offenders = [];
        // Helper: is this node (or any ancestor) a horizontal scroll
        // container? If so, we expect its children to extend past the
        // viewport — that's the author's deliberate rail.
        const insideScrollX = (el) => {
          let n = el;
          while (n && n !== document.body) {
            const cs = window.getComputedStyle(n);
            if (cs.overflowX === "auto" || cs.overflowX === "scroll" ||
                cs.overflowX === "hidden" || cs.overflowX === "clip") {
              return true;
            }
            n = n.parentElement;
          }
          return false;
        };
        const nodes = document.body.querySelectorAll("*");
        for (const el of nodes) {
          const cs = window.getComputedStyle(el);
          if (cs.position === "fixed") continue;
          if (cs.visibility === "hidden" || cs.display === "none") continue;
          if (insideScrollX(el)) continue;
          const r = el.getBoundingClientRect();
          if (r.width === 0 && r.height === 0) continue;
          if (r.right > vw + 1 || r.left < -1) {
            offenders.push({
              tag: el.tagName,
              className:
                typeof el.className === "string" ? el.className : "",
              id: el.id || "",
              right: Math.round(r.right),
              left: Math.round(r.left),
              viewport: vw,
            });
            if (offenders.length >= 20) break;
          }
        }
        if (offenders.length > 0) {
          // eslint-disable-next-line no-console
          console.groupCollapsed(
            `%c[KALKI overflow] ${offenders.length} element(s) past ${vw}px on ${location.pathname}`,
            "color:#FF2E9A;font-weight:600"
          );
          offenders.forEach((o) => {
            // eslint-disable-next-line no-console
            console.warn(o);
          });
          // eslint-disable-next-line no-console
          console.groupEnd();
        } else {
          // eslint-disable-next-line no-console
          console.debug(
            `[KALKI overflow] clean on ${location.pathname} @ ${vw}px`
          );
        }
      });
    };

    // Debounced resize scan.
    let t = 0;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(scan, 180);
    };
    scan();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      window.removeEventListener("resize", onResize);
    };
  }, [location.pathname]);
}
